import { GS } from '../lib/designTokens';

/**
 * NativeWelcome — first screen in the native iOS/Android app for a coach who is
 * NOT signed in. They already downloaded the app from the App Store, so the web
 * marketing funnel ("score the game → import → intel") is the wrong thing to
 * show. This is a brief branded welcome that goes straight to getting started.
 *
 * It does NOT own any auth logic — it reuses the same handlers/overlay that the
 * web LandingPage uses (openCreateAccount / openSignIn / onTryDemo), so there is
 * one source of truth for authentication.
 */
export const NativeWelcome = ({
  onGetStarted,
  onSignIn,
  onTryDemo,
  demoLoading,
  demoError,
}: {
  onGetStarted: () => void;
  onSignIn: () => void;
  onTryDemo: () => void;
  demoLoading: boolean;
  demoError: string;
}) => {
  return (
    <div
      style={{
        minHeight: '100dvh',
        background: GS.bg,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        // Respect the notch / home indicator on iOS.
        paddingTop: 'calc(env(safe-area-inset-top) + 24px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 24px)',
        paddingLeft: 24,
        paddingRight: 24,
      }}
    >
      {/* Brand + value prop, vertically centered in the upper area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', gap: 20 }}>
        <div
          style={{
            width: 84,
            height: 84,
            borderRadius: 22,
            boxShadow: `0 12px 34px ${GS.greenGlow}`,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img src="/dugout-mark.png" alt="Dugout" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>

        <h1 style={{ fontSize: 34, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.02em', color: GS.ink, lineHeight: 1.05 }}>
          Welcome to the smartest dugout in the league.
        </h1>

        <p style={{ fontSize: 16, lineHeight: 1.5, color: GS.ink2, maxWidth: 340, fontWeight: 500 }}>
          Turn your roster into real intel. Build the optimal lineup in seconds.
        </p>
      </div>

      {/* Actions pinned to the bottom — thumb-reachable */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <button
          onClick={onGetStarted}
          style={{ width: '100%', padding: 17, background: `linear-gradient(150deg, ${GS.green}, ${GS.green2})`, color: '#FFFFFF', borderRadius: 14, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', fontSize: 15, border: 'none', cursor: 'pointer', boxShadow: `0 8px 28px ${GS.greenGlow}` }}
        >
          Sign up
        </button>

        <button
          onClick={onSignIn}
          style={{ width: '100%', padding: 17, background: GS.panel, color: GS.ink, borderRadius: 14, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', fontSize: 15, border: `1px solid ${GS.line}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          Sign in
        </button>

        {demoError && <p style={{ color: GS.red, fontSize: 12, fontWeight: 500, textAlign: 'center' }}>{demoError}</p>}

        <button
          onClick={onTryDemo}
          disabled={demoLoading}
          style={{ width: '100%', padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer', color: GS.ink2, fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: demoLoading ? 0.6 : 1 }}
        >
          {demoLoading ? (
            <><span className="animate-spin" style={{ width: 16, height: 16, border: '2px solid rgba(0,0,0,0.2)', borderTopColor: GS.ink2, borderRadius: '50%', display: 'inline-block' }} /> Loading demo…</>
          ) : (
            <>Or try the demo first</>
          )}
        </button>
      </div>
    </div>
  );
};
